import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
import { getSalesHeaders as h } from '../../utils/salesAuth';
import { fmtDate } from '../../utils/dateUtils';
function fmt(v) { return v != null ? `$${Math.round(v).toLocaleString()}` : '$0'; }

const STAGE_LABELS = {
  pipe_fence: 'Pipe Fence', concrete: 'Concrete', metal_building: 'Metal Building',
  land_clearing: 'Land Clearing', dirt_work: 'Dirt Work', gravel: 'Gravel', custom_gate: 'Custom Gate',
};

export default function AIEstimateGenerator({ oppId, onEstimateSaved }) {
  const [step, setStep] = useState('idle'); // idle, interpreting, review_inputs, calculating, review_estimate, saving
  const [additionalText, setAdditionalText] = useState('');
  const [aiInputs, setAiInputs] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [quotedPrice, setQuotedPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [savedEstimates, setSavedEstimates] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/sales/estimates/${oppId}`, { credentials: 'include', headers: h() })
      .then(r => r.ok ? r.json() : []).then(setSavedEstimates);
  }, [oppId]);

  const toggleDeposit = async (estimateId, currentStatus) => {
    const newReceived = currentStatus !== 'received';
    try {
      const res = await fetch(`${API}/api/sales/estimates/${estimateId}/deposit`, {
        method: 'PATCH', credentials: 'include', headers: h(),
        body: JSON.stringify({ deposit_received: newReceived }),
      });
      if (res.ok) {
        setSavedEstimates(prev => prev.map(e =>
          e.id === estimateId ? { ...e, deposit_status: newReceived ? 'received' : 'pending', deposit_received_at: newReceived ? new Date().toISOString() : null } : e
        ));
      }
    } catch (err) { console.error('Deposit update failed:', err); }
  };

  const copySignLink = (token, id) => {
    navigator.clipboard.writeText(`${window.location.origin}/quote/sign/${token}`);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const downloadPdf = async (estimateId, quoteNum) => {
    try {
      const res = await fetch(`${API}/api/sales/estimates/${estimateId}/pdf`, { credentials: 'include', headers: h() });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${quoteNum || 'NTX-Quote'}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) { console.error('PDF download failed:', e); }
  };

  const interpret = async () => {
    setStep('interpreting');
    setError('');
    try {
      const res = await fetch(`${API}/api/sales/ai/interpret-estimate`, {
        method: 'POST', credentials: 'include', headers: h(),
        body: JSON.stringify({ opportunity_id: oppId, additional_text: additionalText }),
      });
      const data = await res.json();
      if (res.ok) {
        setAiInputs(data);
        setStep('review_inputs');
      } else {
        setError(data.detail || 'Failed');
        setStep('idle');
      }
    } catch { setError('Network error'); setStep('idle'); }
  };

  const calculate = async () => {
    setStep('calculating');
    setError('');
    try {
      const res = await fetch(`${API}/api/sales/ai/generate-estimate`, {
        method: 'POST', credentials: 'include', headers: h(),
        body: JSON.stringify({ opportunity_id: oppId, ai_inputs: aiInputs }),
      });
      const data = await res.json();
      if (res.ok) {
        setEstimate(data);
        setQuotedPrice(String(data.calculated_target));
        setStep('review_estimate');
      } else {
        setError(data.detail || 'Failed');
        setStep('review_inputs');
      }
    } catch { setError('Network error'); setStep('review_inputs'); }
  };

  const save = async () => {
    setStep('saving');
    const price = parseFloat(quotedPrice);
    if (isNaN(price) || price <= 0) { setError('Enter a valid price'); setStep('review_estimate'); return; }
    try {
      const res = await fetch(`${API}/api/sales/estimates/save`, {
        method: 'POST', credentials: 'include', headers: h(),
        body: JSON.stringify({
          opportunity_id: oppId,
          estimate_data: estimate,
          quoted_price: price,
          notes,
          payment_schedule: {
            deposit_percent: 50,
            deposit_amount: Math.round(price * 0.50 * 100) / 100,
            progress_percent: 30,
            progress_amount: Math.round(price * 0.30 * 100) / 100,
            final_percent: 20,
            final_amount: Math.round(price * 0.20 * 100) / 100,
            deposit_terms: 'Due upon acceptance of the estimate or proposal',
            progress_terms: 'Due upon delivery of materials, mobilization, or designated milestone',
            final_terms: 'Due upon substantial completion of the contracted scope of work',
          },
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setSavedEstimates(prev => [data, ...prev]);
        setStep('idle');
        setEstimate(null);
        setAiInputs(null);
        setAdditionalText('');
        setNotes('');
        if (onEstimateSaved) onEstimateSaved(data);
      } else {
        setError(data.detail || 'Save failed');
        setStep('review_estimate');
      }
    } catch { setError('Network error'); setStep('review_estimate'); }
  };

  const calcTarget = estimate?.calculated_target || 0;
  const priceNum = parseFloat(quotedPrice) || 0;
  const delta = priceNum - calcTarget;
  const deltaPct = calcTarget > 0 ? ((delta / calcTarget) * 100).toFixed(1) : 0;

  return (
    <div data-testid="ai-estimate-generator" style={{ marginTop: 16 }}>
      {/* Saved Estimates History */}
      {savedEstimates.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <button onClick={() => setShowHistory(!showHistory)} data-testid="toggle-estimate-history"
            style={{ fontSize: 12, color: '#4338ca', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
            {showHistory ? 'Hide' : 'Show'} Estimate History ({savedEstimates.length})
          </button>
          {showHistory && (
            <div style={{ marginTop: 8, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
              {savedEstimates.map(e => {
                // Quote expiration logic
                let expiryBadge = null;
                if (e.valid_through && !e.signature) {
                  const now = new Date();
                  const expiry = new Date(e.valid_through);
                  const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
                  if (daysLeft < 0) {
                    expiryBadge = { text: 'Expired', bg: '#fef2f2', color: '#dc2626' };
                  } else if (daysLeft <= 3) {
                    expiryBadge = { text: `${daysLeft}d left`, bg: '#fef2f2', color: '#dc2626' };
                  } else if (daysLeft <= 7) {
                    expiryBadge = { text: `${daysLeft}d left`, bg: '#fef9c3', color: '#ca8a04' };
                  }
                }

                const isSigned = !!e.signature;
                const depositStatus = e.deposit_status || 'pending';
                const depositAmt = e.payment_schedule?.deposit_amount || (e.quoted_price * 0.5);

                return (
                <div key={e.id} style={{ padding: '10px 14px', borderBottom: '1px solid #e2e8f0', fontSize: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                    <span style={{ fontWeight: 700, color: '#1e293b' }}>{e.quote_number}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                      {/* Expiration badge */}
                      {expiryBadge && (
                        <span data-testid={`expiry-badge-${e.id}`} style={{ padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: expiryBadge.bg, color: expiryBadge.color }}>
                          {expiryBadge.text}
                        </span>
                      )}
                      {/* Signature status */}
                      {isSigned ? (
                        <span data-testid={`signed-badge-${e.id}`} style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: '#dcfce7', color: '#16a34a' }}>Signed</span>
                      ) : (
                        <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: '#fef9c3', color: '#ca8a04' }}>{e.status || 'Draft'}</span>
                      )}
                      {/* Deposit tracker (only for signed quotes) */}
                      {isSigned && (
                        <button data-testid={`deposit-toggle-${e.id}`}
                          onClick={() => toggleDeposit(e.id, depositStatus)}
                          title={depositStatus === 'received' ? `Deposit received: ${fmt(depositAmt)}` : `Click to mark deposit received: ${fmt(depositAmt)}`}
                          style={{
                            padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer', border: 'none',
                            background: depositStatus === 'received' ? '#dcfce7' : '#fef2f2',
                            color: depositStatus === 'received' ? '#16a34a' : '#dc2626',
                          }}>
                          {depositStatus === 'received' ? `Deposit ${fmt(depositAmt)}` : `Deposit Due ${fmt(depositAmt)}`}
                        </button>
                      )}
                      <span style={{ fontWeight: 700, color: '#16a34a' }}>{fmt(e.quoted_price)}</span>
                      <button data-testid={`download-pdf-${e.id}`} onClick={() => downloadPdf(e.id, e.quote_number)}
                        style={{ padding: '5px 10px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer', minHeight: 28 }}>
                        PDF
                      </button>
                      {e.signature_token && !isSigned && (
                        <button data-testid={`copy-sign-link-${e.id}`}
                          onClick={() => copySignLink(e.signature_token, e.id)}
                          style={{ padding: '5px 10px', background: copiedId === e.id ? '#16a34a' : '#4338ca', color: '#fff', border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s', minHeight: 28 }}>
                          {copiedId === e.id ? 'Copied!' : 'Copy Sign Link'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{ color: '#64748b', marginTop: 2 }}>
                    {(e.services || []).map(s => STAGE_LABELS[s] || s).join(', ')} • {fmtDate(e.created_at)}
                    {e.override && (
                      <span style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 3, fontSize: 10, background: e.override.delta_dollars < 0 ? '#fef2f2' : '#f0fdf4', color: e.override.delta_dollars < 0 ? '#dc2626' : '#16a34a' }}>
                        {e.override.delta_dollars > 0 ? '+' : ''}{fmt(e.override.delta_dollars)} ({e.override.delta_percent > 0 ? '+' : ''}{e.override.delta_percent}%)
                      </span>
                    )}
                    {e.ai_assisted && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 3, fontSize: 10, background: '#eef2ff', color: '#4338ca' }}>AI</span>}
                    {isSigned && <span style={{ marginLeft: 6, fontSize: 10, color: '#16a34a' }}>Signed by {e.signature.typed_name}</span>}
                    {isSigned && depositStatus === 'received' && e.deposit_received_at && (
                      <span style={{ marginLeft: 6, fontSize: 10, color: '#16a34a' }}>• Deposit {fmtDate(e.deposit_received_at)}</span>
                    )}
                  </div>
                  {e.notes && <div style={{ color: '#94a3b8', marginTop: 2, fontStyle: 'italic' }}>{e.notes}</div>}
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Generate Estimate Button */}
      {step === 'idle' && (
        <div style={{ background: '#fafbff', border: '1px solid #e0e7ff', borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#4338ca', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 20, height: 20, background: '#4338ca', borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 800 }}>AI</span>
            Generate Estimate
          </div>
          <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 10px' }}>
            AI will interpret the project details and calculate pricing using the official pricing engine. You review and approve before anything is finalized.
          </p>
          <textarea data-testid="estimate-additional-text" value={additionalText} onChange={e => setAdditionalText(e.target.value)}
            placeholder="Optional: Add project details not already in the CRM (e.g. '740 feet of 5-foot top rail no-climb fence with two 12-foot gates painted black')"
            rows={2} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, resize: 'vertical', boxSizing: 'border-box', marginBottom: 10 }} />
          <button data-testid="generate-estimate-btn" onClick={interpret}
            style={{ padding: '10px 18px', background: '#4338ca', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 44 }}>
            Generate Estimate
          </button>
        </div>
      )}

      {step === 'interpreting' && <LoadingState text="AI is interpreting project details..." />}

      {/* Review AI Inputs */}
      {step === 'review_inputs' && aiInputs && (
        <div style={{ background: '#fff', border: '1px solid #e0e7ff', borderRadius: 8, padding: 14 }}>
          <h4 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', margin: '0 0 10px' }}>Review AI Interpretation</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
              background: aiInputs.confidence === 'high' ? '#dcfce7' : aiInputs.confidence === 'medium' ? '#fef9c3' : '#fef2f2',
              color: aiInputs.confidence === 'high' ? '#16a34a' : aiInputs.confidence === 'medium' ? '#ca8a04' : '#dc2626',
            }}>Confidence: {aiInputs.confidence?.toUpperCase()}</span>
          </div>

          {/* Services extracted */}
          {Object.entries(aiInputs.services || {}).map(([svc, data]) => (
            <div key={svc} style={{ marginBottom: 10, padding: '10px 12px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#4338ca', marginBottom: 6 }}>{STAGE_LABELS[svc] || svc}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 4 }}>
                {Object.entries(data).map(([k, v]) => (
                  <div key={k} style={{ fontSize: 11 }}>
                    <span style={{ color: '#64748b' }}>{k.replace(/_/g, ' ')}: </span>
                    <span style={{ color: '#1e293b', fontWeight: 600 }}>{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Missing info */}
          {aiInputs.missing_info?.length > 0 && (
            <div style={{ background: '#fefce8', border: '1px solid #fde68a', padding: 10, borderRadius: 6, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>Additional Information Recommended:</div>
              {aiInputs.missing_info.map((m, i) => <div key={i} style={{ fontSize: 11, color: '#78350f', paddingLeft: 8 }}>• {m}</div>)}
            </div>
          )}

          {aiInputs.interpretation_notes && (
            <p style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic', margin: '0 0 10px' }}>{aiInputs.interpretation_notes}</p>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button data-testid="run-pricing-engine-btn" onClick={calculate}
              style={{ padding: '10px 16px', background: '#4338ca', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 44 }}>
              Calculate Price
            </button>
            <button onClick={() => { setStep('idle'); setAiInputs(null); }}
              style={{ padding: '10px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#64748b', minHeight: 44 }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === 'calculating' && <LoadingState text="Pricing engine calculating..." />}

      {/* Review Estimate */}
      {step === 'review_estimate' && estimate && (
        <div style={{ background: '#fff', border: '1px solid #e0e7ff', borderRadius: 8, padding: 14 }}>
          <h4 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Review Estimate</h4>

          {/* Pricing Breakdown */}
          <div style={{ background: '#f8fafc', borderRadius: 6, padding: 12, marginBottom: 12, border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px 16px', fontSize: 12 }}>
              <span style={{ color: '#64748b' }}>Base Total</span><span style={{ textAlign: 'right', color: '#1e293b', fontWeight: 500 }}>{fmt(estimate.base_total)}</span>
              <span style={{ color: '#64748b' }}>Options</span><span style={{ textAlign: 'right', color: '#1e293b', fontWeight: 500 }}>{fmt(estimate.options_total)}</span>
              {estimate.site_adjustments > 0 && <><span style={{ color: '#64748b' }}>Site Adjustments</span><span style={{ textAlign: 'right' }}>{fmt(estimate.site_adjustments)}</span></>}
              <span style={{ color: '#64748b' }}>Fixed Fees</span><span style={{ textAlign: 'right', color: '#1e293b', fontWeight: 500 }}>{fmt(estimate.fixed_fees)}</span>
              {estimate.package_adjustment !== 0 && <><span style={{ color: '#16a34a' }}>Package Discount</span><span style={{ textAlign: 'right', color: '#16a34a' }}>{fmt(estimate.package_adjustment)}</span></>}
              <div style={{ gridColumn: '1 / -1', borderTop: '2px solid #cbd5e1', margin: '4px 0' }} />
              <span style={{ color: '#1e293b', fontWeight: 700 }}>Calculated Target</span><span style={{ textAlign: 'right', color: '#1e293b', fontWeight: 700, fontSize: 14 }}>{fmt(estimate.calculated_target)}</span>
              <span style={{ color: '#64748b' }}>Customer Range</span><span style={{ textAlign: 'right', color: '#475569' }}>{fmt(estimate.customer_range?.low)} – {fmt(estimate.customer_range?.high)}</span>
            </div>
          </div>

          {/* Flags */}
          {estimate.flags?.length > 0 && (
            <div style={{ background: '#fefce8', border: '1px solid #fde68a', padding: 8, borderRadius: 6, marginBottom: 10 }}>
              {estimate.flags.map((f, i) => <div key={i} style={{ fontSize: 11, color: '#92400e' }}>⚠ {f}</div>)}
            </div>
          )}

          {/* Quoted Price (editable) */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Quoted Price</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16, color: '#475569' }}>$</span>
              <input data-testid="quoted-price-input" type="number" value={quotedPrice} onChange={e => setQuotedPrice(e.target.value)}
                style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontWeight: 700, width: 160 }} />
              {delta !== 0 && calcTarget > 0 && (
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4,
                  background: delta < 0 ? '#fef2f2' : '#f0fdf4', color: delta < 0 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                  {delta > 0 ? '+' : ''}{fmt(delta)} ({deltaPct > 0 ? '+' : ''}{deltaPct}%)
                  {delta < 0 && Math.abs(deltaPct) > 10 && ' — Approval may be required'}
                </span>
              )}
            </div>
          </div>

          {/* Payment Schedule */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Payment Schedule (50/30/20)</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: '#475569' }}>50% Initial Deposit</span>
                <span style={{ fontWeight: 600, color: '#1e293b' }}>{fmt(priceNum * 0.50)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: '#475569' }}>30% Progress Payment</span>
                <span style={{ fontWeight: 600, color: '#1e293b' }}>{fmt(priceNum * 0.30)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: '#475569' }}>20% Final Payment</span>
                <span style={{ fontWeight: 600, color: '#1e293b' }}>{fmt(priceNum * 0.20)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Notes / Reason for adjustment</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Optional notes..."
              style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }} />
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button data-testid="save-estimate-btn" onClick={save}
              style={{ padding: '10px 18px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 44 }}>
              Save Estimate
            </button>
            <button onClick={() => setStep('review_inputs')}
              style={{ padding: '10px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#64748b', minHeight: 44 }}>
              Back
            </button>
            <button onClick={() => { setStep('idle'); setEstimate(null); setAiInputs(null); }}
              style={{ padding: '10px 14px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#64748b', minHeight: 44 }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === 'saving' && <LoadingState text="Saving estimate..." />}
      {error && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{error}</p>}
    </div>
  );
}

function LoadingState({ text }) {
  return (
    <div style={{ padding: 20, textAlign: 'center', color: '#4338ca', fontSize: 13, background: '#fafbff', border: '1px solid #e0e7ff', borderRadius: 8 }}>
      <div style={{ marginBottom: 6, fontSize: 18 }}>⏳</div>{text}
    </div>
  );
}
